import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { formatPrice } from '../lib/utils';
import { toast } from 'sonner';
import { 
  ChevronLeft, 
  Zap, 
  AlertCircle,
  Wallet,
  QrCode,
  Building2,
  ArrowRight
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PAYMENT_METHODS = [
  { id: 'qris', name: 'QRIS', icon: QrCode, description: 'GoPay, OVO, Dana, LinkAja' },
  { id: 'va_bca', name: 'Virtual Account BCA', icon: Building2, description: 'BCA Transfer' },
  { id: 'va_bni', name: 'Virtual Account BNI', icon: Building2, description: 'BNI Transfer' },
  { id: 'va_mandiri', name: 'Virtual Account Mandiri', icon: Building2, description: 'Mandiri Transfer' },
];

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDenom, setSelectedDenom] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [userId, setUserId] = useState('');
  const [serverId, setServerId] = useState('');
  const [email, setEmail] = useState(user?.email || '');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchProduct();
  }, [slug]);

  useEffect(() => {
    if (user?.email) {
      setEmail(user.email);
    }
  }, [user]);

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/products/${slug}`);
      setProduct(response.data.product);
    } catch (error) {
      console.error('Failed to fetch product:', error);
      toast.error('Product not found');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const getPrice = (denom) => {
    if (user?.role === 'reseller') {
      return denom.reseller_price;
    }
    return denom.price;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedDenom) {
      toast.error('Please select a denomination first');
      return;
    }

    if (!selectedPayment) {
      toast.error('Please select a payment method');
      return;
    }

    if (product.input_fields.includes('user_id') && !userId) {
      toast.error('Enter User ID');
      return;
    }

    if (product.input_fields.includes('server_id') && !serverId) {
      toast.error('Enter Server ID');
      return;
    }

    if (!email) {
      toast.error('Enter email');
      return;
    }

    setSubmitting(true);

    try {
      const orderData = {
        product_id: product.id,
        denomination_id: selectedDenom.id,
        game_user_id: userId,
        game_server_id: serverId || null,
        email: email,
        payment_method: selectedPayment.id
      };

      const endpoint = user 
        ? `${API_URL}/api/orders/authenticated`
        : `${API_URL}/api/orders/guest`;

      const headers = user 
        ? { Authorization: `Bearer ${token}` }
        : {};

      const response = await axios.post(endpoint, orderData, { headers });
      const order = response.data.order;

      toast.success('Order created successfully!');
      navigate(`/checkout/${order.id}`, { state: { order } });
    } catch (error) {
      console.error('Failed to create order:', error);
      toast.error(error.response?.data?.detail || 'Failed to create order');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-16 flex items-center justify-center bg-[#09090b]">
        <div className="animate-spin w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!product) {
    return null;
  }

  return (
    <div 
      className="min-h-screen bg-cover bg-center bg-[#09090b] text-zinc-50 relative pt-24 pb-16"
      style={{ backgroundImage: "url('/hero-bg.png')" }}
    >
      <div className="absolute inset-0 bg-gradient-to-b from-[#09090b]/80 via-[#09090b]/90 to-[#09090b] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* Widescreen 3-Column Terminal Deck */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          
          {/* COLUMN 1: System Telemetry & Brand Manifest (col-span-3) */}
          <div className="lg:col-span-3 space-y-6">
            
            {/* Back Button & Brand Card */}
            <div className="relative overflow-hidden rounded-2xl border border-zinc-850 bg-zinc-950/75 backdrop-blur-md p-5 shadow-2xl">
              <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/5 via-transparent to-cyan-500/5 pointer-events-none" />
              <Button
                variant="ghost"
                size="sm"
                className="text-zinc-400 hover:text-white mb-5 text-xs transition-colors p-0 hover:bg-transparent"
                onClick={() => navigate('/')}
                data-testid="back-btn"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back to products
              </Button>
              
              <div className="flex items-center gap-4">
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-14 h-14 rounded-2xl object-cover border border-emerald-500/20 shadow-[0_0_20px_rgba(16,185,129,0.15)]"
                />
                <div>
                  <h1 className="font-bold text-lg text-white tracking-wide uppercase leading-tight">
                    {product.name}
                  </h1>
                  <span className="text-[9px] font-mono text-emerald-450 tracking-widest uppercase bg-emerald-500/10 px-2 py-0.5 rounded border border-emerald-500/20 mt-1 inline-block">
                    {product.category}
                  </span>
                </div>
              </div>
            </div>

            {/* Diagnostics Telemetry */}
            <div className="bg-zinc-950/45 backdrop-blur-md rounded-2xl p-5 border border-zinc-850 shadow-xl relative overflow-hidden font-mono text-[11px] space-y-3">
              <div className="absolute top-0 left-0 w-16 h-0.5 bg-cyan-500" />
              <div className="text-[10px] text-zinc-500 tracking-wider uppercase mb-1">SYSTEM_TELEMETRY</div>
              <div className="flex justify-between items-center border-b border-zinc-900/60 pb-1.5">
                <span className="text-zinc-400">NODE_STATUS</span>
                <span className="text-emerald-400 flex items-center gap-1.5 font-bold">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> ONLINE
                </span>
              </div>
              <div className="flex justify-between items-center border-b border-zinc-900/60 pb-1.5">
                <span className="text-zinc-400">LATENCY</span>
                <span className="text-cyan-400">14 ms</span>
              </div>
              <div className="flex justify-between items-center border-b border-zinc-900/60 pb-1.5">
                <span className="text-zinc-400">ENCRYPTION</span>
                <span className="text-zinc-350">AES-256</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-zinc-400">CORE_SYNC</span>
                <span className="text-emerald-400">ESTABLISHED</span>
              </div>
            </div>

            {/* Instructions */}
            {product.instructions && (
              <div className="bg-emerald-950/15 border border-emerald-500/15 rounded-2xl p-4">
                <div className="flex items-start gap-3">
                  <Zap className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5 animate-pulse" />
                  <p className="text-xs text-zinc-350 leading-relaxed font-sans">{product.instructions}</p>
                </div>
              </div>
            )}

            {/* Console Manifest (Sticky Order Summary) */}
            <div className="bg-zinc-950/75 backdrop-blur-md rounded-2xl p-5 border border-zinc-850 shadow-2xl relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/5 rounded-full blur-3xl pointer-events-none" />
              
              <h2 className="font-bold text-xs text-white mb-5 uppercase tracking-widest flex items-center gap-2 border-b border-zinc-850 pb-3">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                Console Manifest
              </h2>

              <div className="space-y-4 mb-5 text-xs font-mono">
                <div>
                  <span className="text-zinc-550 tracking-wider text-[10px]">SELECTED_PRODUCT</span>
                  <p className="font-bold text-white uppercase mt-0.5">{product.name}</p>
                  {selectedDenom ? (
                    <p className="text-emerald-400 tracking-wider text-[11px] mt-0.5">{selectedDenom.name}</p>
                  ) : (
                    <p className="text-zinc-650 tracking-wider text-[11px] mt-0.5">PENDING_NOMINAL</p>
                  )}
                </div>

                {(userId || serverId) && (
                  <div className="bg-zinc-900/40 rounded-xl p-3 border border-zinc-850 space-y-1.5 text-[11px]">
                    {userId && (
                      <p className="text-zinc-400 flex justify-between">
                        <span>GATEWAY_ID:</span> 
                        <span className="text-white font-bold">{userId}</span>
                      </p>
                    )}
                    {serverId && (
                      <p className="text-zinc-400 flex justify-between">
                        <span>ROUTER_ID:</span> 
                        <span className="text-white font-bold">{serverId}</span>
                      </p>
                    )}
                  </div>
                )}

                <div className="border-t border-zinc-850 pt-3">
                  <span className="text-zinc-550 tracking-wider text-[10px]">PAYMENT_GATEWAY</span>
                  {selectedPayment ? (
                    <p className="text-white uppercase font-bold mt-0.5">{selectedPayment.name}</p>
                  ) : (
                    <p className="text-zinc-650 tracking-wider text-[11px] mt-0.5">PENDING_GATEWAY</p>
                  )}
                </div>
              </div>

              <div className="border-t border-zinc-805 pt-4 mb-5">
                <div className="flex justify-between items-center">
                  <span className="text-[10px] text-zinc-400 font-mono tracking-wider">TOTAL_ESTIMATE</span>
                  <span className="text-lg font-extrabold text-white font-mono">
                    {selectedDenom ? formatPrice(getPrice(selectedDenom)) : 'Rp 0'}
                  </span>
                </div>
              </div>
            </div>

          </div>

          {/* COLUMN 2: Select Synapse Nominal (col-span-5) */}
          <div className="lg:col-span-5 space-y-6">
            <div className="bg-zinc-900/40 backdrop-blur-md rounded-2xl p-6 border border-zinc-800 shadow-xl relative overflow-hidden">
              <div className="absolute top-0 left-0 w-24 h-1 bg-gradient-to-r from-emerald-500 to-transparent" />
              <h2 className="font-bold text-sm text-white mb-6 uppercase tracking-widest flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-[10px] flex items-center justify-center font-mono">01</span>
                Select Synapse Nominal
              </h2>
              <div className="grid grid-cols-2 gap-4">
                {product.denominations.map((denom) => {
                  const isSelected = selectedDenom?.id === denom.id;
                  return (
                    <button
                      key={denom.id}
                      className={`p-4 rounded-xl text-left border transition-all duration-300 relative overflow-hidden flex flex-col justify-between min-h-[96px] ${
                        isSelected
                          ? 'border-emerald-500/80 bg-emerald-500/5 shadow-[0_0_15px_rgba(16,185,129,0.15)]'
                          : 'border-zinc-800 bg-zinc-950/60 hover:border-zinc-700 hover:bg-zinc-900/30'
                      }`}
                      onClick={() => setSelectedDenom(denom)}
                      data-testid={`denom-${denom.id}`}
                    >
                      {isSelected && (
                        <div className="absolute top-0 right-0 w-8 h-8 bg-emerald-500/10 border-b border-l border-emerald-500/20 rounded-bl-lg flex items-center justify-center">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                        </div>
                      )}
                      <p className="font-bold text-white text-xs tracking-wider uppercase">{denom.name}</p>
                      <div>
                        <p className="text-md font-extrabold text-emerald-400 font-mono tracking-wide mt-2">
                          {formatPrice(getPrice(denom))}
                        </p>
                        {user?.role === 'reseller' && denom.price !== denom.reseller_price && (
                          <p className="text-[10px] text-zinc-500 line-through font-mono mt-0.5">
                            {formatPrice(denom.price)}
                          </p>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* COLUMN 3: Identity, Payment Gateways & Submit Deck (col-span-4) */}
          <div className="lg:col-span-4 space-y-6">
            
            {/* Identity Gateway */}
            {product.input_fields.length > 0 && (
              <div className="bg-zinc-900/40 backdrop-blur-md rounded-2xl p-6 border border-zinc-800 shadow-xl relative overflow-hidden">
                <div className="absolute top-0 left-0 w-24 h-1 bg-gradient-to-r from-emerald-500 to-transparent" />
                <h2 className="font-bold text-sm text-white mb-6 uppercase tracking-widest flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-[10px] flex items-center justify-center font-mono">02</span>
                  Target Identity Gateway
                </h2>
                <div className="space-y-4">
                  {product.input_fields.includes('user_id') && (
                    <div className="space-y-2">
                      <Label htmlFor="userId" className="text-[10px] font-mono text-zinc-400 tracking-widest uppercase">User ID</Label>
                      <Input
                        id="userId"
                        type="text"
                        placeholder="Enter User ID"
                        className="bg-zinc-950/80 border-zinc-800 text-white focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500/40 text-sm rounded-lg h-12 px-4 transition-all"
                        value={userId}
                        onChange={(e) => setUserId(e.target.value)}
                        data-testid="user-id-input"
                      />
                    </div>
                  )}
                  {product.input_fields.includes('server_id') && (
                    <div className="space-y-2">
                      <Label htmlFor="serverId" className="text-[10px] font-mono text-zinc-400 tracking-widest uppercase">Server ID</Label>
                      <Input
                        id="serverId"
                        type="text"
                        placeholder="Enter Server ID"
                        className="bg-zinc-950/80 border-zinc-800 text-white focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500/40 text-sm rounded-lg h-12 px-4 transition-all"
                        value={serverId}
                        onChange={(e) => setServerId(e.target.value)}
                        data-testid="server-id-input"
                      />
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Select Gateway */}
            <div className="bg-zinc-900/40 backdrop-blur-md rounded-2xl p-6 border border-zinc-800 shadow-xl relative overflow-hidden">
              <div className="absolute top-0 left-0 w-24 h-1 bg-gradient-to-r from-cyan-500 to-transparent" />
              <h2 className="font-bold text-sm text-white mb-6 uppercase tracking-widest flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-[10px] flex items-center justify-center font-mono">03</span>
                Select Synapse Gateway
              </h2>
              <div className="space-y-3">
                {PAYMENT_METHODS.map((method) => {
                  const Icon = method.icon;
                  const isSelected = selectedPayment?.id === method.id;
                  return (
                    <button
                      key={method.id}
                      className={`w-full p-3.5 rounded-xl flex items-center gap-4 border transition-all duration-300 ${
                        isSelected
                          ? 'border-cyan-500/80 bg-cyan-500/5 shadow-[0_0_15px_rgba(6,182,212,0.15)]'
                          : 'border-zinc-800 bg-zinc-950/60 hover:border-zinc-700 hover:bg-zinc-900/30'
                      }`}
                      onClick={() => setSelectedPayment(method)}
                      data-testid={`pay-${method.id}`}
                    >
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 border transition-colors ${
                        isSelected ? 'bg-cyan-500/10 border-cyan-500/30' : 'bg-zinc-900 border-zinc-800'
                      }`}>
                        <Icon className={`w-4 h-4 ${isSelected ? 'text-cyan-400' : 'text-zinc-400'}`} />
                      </div>
                      <div className="text-left">
                        <p className="font-bold text-white text-xs tracking-wider uppercase leading-none">{method.name}</p>
                        <p className="text-[10px] text-zinc-500 mt-1 leading-normal font-sans">{method.description}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Email Gateway */}
            <div className="bg-zinc-900/40 backdrop-blur-md rounded-2xl p-6 border border-zinc-800 shadow-xl relative overflow-hidden">
              <div className="absolute top-0 left-0 w-24 h-1 bg-gradient-to-r from-emerald-500 to-transparent" />
              <h2 className="font-bold text-sm text-white mb-6 uppercase tracking-widest flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-[10px] flex items-center justify-center font-mono">04</span>
                Receipt Gateway
              </h2>
              <div className="space-y-3">
                <Label htmlFor="email" className="text-[10px] font-mono text-zinc-400 tracking-widest uppercase">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter email for receipt"
                  className="bg-zinc-950/80 border-zinc-800 text-white focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500/40 text-sm rounded-lg h-12 px-4 transition-all"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="email-input"
                />
              </div>
            </div>

            {/* Bayar Sekarang Button */}
            <Button
              className="w-full bg-emerald-500 hover:bg-emerald-400 hover:shadow-[0_0_20px_rgba(16,185,129,0.25)] text-zinc-950 font-extrabold py-6 rounded-xl text-xs tracking-widest uppercase transition-all duration-300"
              onClick={handleSubmit}
              disabled={submitting || !selectedDenom || !selectedPayment}
              data-testid="order-btn"
            >
              {submitting ? (
                <span className="flex items-center gap-2 justify-center font-sans">
                  <Loader2 className="w-4 h-4 animate-spin text-zinc-950" />
                  TRANSACTING...
                </span>
              ) : (
                <span className="flex items-center gap-2 justify-center font-sans">
                  <Zap className="w-4 h-4 fill-current animate-pulse" />
                  Bayar Sekarang
                </span>
              )}
            </Button>

          </div>

        </div>
      </div>
    </div>
  );
}
